import React, { useState, useEffect, useCallback } from 'react';
import { biApi, enhancedBiApi, biLoungeApi } from '../lib/api';
import { formatCurrency, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend, Treemap
} from 'recharts';
import {
  ChartLineUp, Buildings, CurrencyDollar, Users, Plus, Pencil, Trash,
  Target, Lightning, Funnel, TrendUp, Handshake, ClipboardText, Folder, Briefcase, Truck, Package, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const COLORS = ['#0047FF', '#10B981', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#F97316'];

export default function BusinessIntelligencePage() {
  const [units, setUnits] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [acquisition, setAcquisition] = useState(null);
  const [crossSell, setCrossSell] = useState([]);
  const [competitive, setCompetitive] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    name: '', sector: '', description: '', revenue_target: 0, cost_budget: 0, services: [], target_markets: []
  });
  const [serviceInput, setServiceInput] = useState('');
  const [marketInput, setMarketInput] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [unitsRes, forecastRes, acqRes, crossRes, compRes, kpisRes] = await Promise.all([
        biApi.getBusinessUnits(),
        biApi.getRevenueForecast(),
        biApi.getClientAcquisition(),
        enhancedBiApi.getCrossSell().catch(() => ({ data: [] })),
        enhancedBiApi.getCompetitive().catch(() => ({ data: null })),
        biLoungeApi.kpis().catch(() => ({ data: null })),
      ]);
      setUnits(unitsRes.data);
      setForecast(forecastRes.data);
      setAcquisition(acqRes.data);
      setCrossSell(crossRes.data || []);
      setCompetitive(compRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      toast.error('Failed to load BI data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await biApi.updateBusinessUnit(editingUnit.id, formData);
        toast.success('Business unit updated');
      } else {
        await biApi.createBusinessUnit(formData);
        toast.success('Business unit created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business unit?')) return;
    try {
      await biApi.deleteBusinessUnit(id);
      toast.success('Business unit deleted');
      fetchData();
    } catch (error) { toast.error('Delete failed'); }
  };

  const openEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name, sector: unit.sector || '', description: unit.description || '',
      revenue_target: unit.revenue_target || 0, cost_budget: unit.cost_budget || 0,
      services: unit.services || [], target_markets: unit.target_markets || []
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUnit(null);
    setFormData({ name: '', sector: '', description: '', revenue_target: 0, cost_budget: 0, services: [], target_markets: [] });
    setServiceInput(''); setMarketInput('');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  const funnelData = forecast?.conversion_funnel ? [
    { name: 'Lead', value: forecast.conversion_funnel.lead || 0 },
    { name: 'Qualified', value: forecast.conversion_funnel.qualified || 0 },
    { name: 'Proposal', value: forecast.conversion_funnel.proposal || 0 },
    { name: 'Negotiation', value: forecast.conversion_funnel.negotiation || 0 },
    { name: 'Won', value: forecast.conversion_funnel.closed_won || 0 },
  ] : [];

  const sectorForecastData = forecast?.sector_forecast ? Object.entries(forecast.sector_forecast).map(([k, v]) => ({
    name: PROCUREMENT_SECTORS.find(s => s.id === k)?.label || k,
    pipeline: v.pipeline, weighted: v.weighted, won: v.won
  })) : [];

  return (
    <div className="space-y-6" data-testid="bi-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Business Intelligence Lounge</h1>
          <p className="text-muted-foreground text-sm mt-1">Cross-module KPIs · Revenue · Client acquisition · Operations</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="add-bu-btn">
          <Plus size={18} className="mr-2" /> New Business Unit
        </Button>
      </div>

      {/* SAP-style Insights Tiles — gradient cards with big numbers */}
      {kpis && (
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Insights</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="bi-insights">
            <InsightTile color="blue"    icon={Handshake}    label="Pipeline"         value={formatCurrency(kpis.crm?.pipeline_value || 0)} sub={`${kpis.crm?.deals || 0} deals`} />
            <InsightTile color="green"   icon={TrendUp}      label="Weighted"         value={formatCurrency(kpis.crm?.weighted_pipeline || 0)} sub={`${kpis.crm?.win_rate || 0}% win`} />
            <InsightTile color="purple"  icon={CurrencyDollar} label="Won Revenue"    value={formatCurrency(kpis.crm?.won_value || 0)} sub="Closed deals" />
            <InsightTile color="orange"  icon={Users}        label="Contacts"         value={kpis.crm?.contacts || 0} sub={`${kpis.crm?.companies || 0} companies`} />
            <InsightTile color="teal"    icon={ClipboardText} label="Task Completion" value={`${kpis.tasks?.completion_rate || 0}%`} sub={`${kpis.tasks?.overdue || 0} overdue`} />
            <InsightTile color="amber"   icon={Folder}       label="Documents"        value={kpis.documents?.docs || 0} sub={`${kpis.documents?.data_rooms || 0} rooms`} />
            <InsightTile color="indigo"  icon={Briefcase}    label="Active Projects"  value={kpis.projects?.active || 0} sub={`${kpis.projects?.total || 0} total`} />
            <InsightTile color="red"     icon={Warning}      label="Low Stock"        value={kpis.supply_chain?.low_stock_items || 0} sub="Reorder needed" />
            <InsightTile color="magenta" icon={Truck}        label="Pending POs"      value={kpis.supply_chain?.pending_pos || 0} sub="Awaiting delivery" />
          </div>
        </div>
      )}

      {/* Legacy KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={CurrencyDollar} label="Weighted Forecast" value={formatCurrency(forecast?.total_weighted_forecast || 0)} sub="Pipeline-adjusted" />
        <KPICard icon={ChartLineUp} label="Total Pipeline" value={formatCurrency(forecast?.total_pipeline || 0)} sub="Open deals" />
        <KPICard icon={Target} label="Win Rate" value={`${(forecast?.win_rate || 0).toFixed(1)}%`} sub="Closed deals" />
        <KPICard icon={Users} label="Total Contacts" value={acquisition?.total_contacts || 0} sub="All leads" />
      </div>

      <Tabs defaultValue="forecast">
        <TabsList>
          <TabsTrigger value="forecast"><ChartLineUp size={16} className="mr-1" /> Revenue Forecast</TabsTrigger>
          <TabsTrigger value="funnel"><Funnel size={16} className="mr-1" /> Conversion Funnel</TabsTrigger>
          <TabsTrigger value="crosssell"><Lightning size={16} className="mr-1" /> Cross-Sell</TabsTrigger>
          <TabsTrigger value="competitive"><Target size={16} className="mr-1" /> Competitive</TabsTrigger>
          <TabsTrigger value="units"><Buildings size={16} className="mr-1" /> Business Units</TabsTrigger>
          <TabsTrigger value="acquisition"><Users size={16} className="mr-1" /> Client Acquisition</TabsTrigger>
        </TabsList>

        <TabsContent value="forecast" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Sector Revenue Forecast</h3>
            <div className="h-80">
              {sectorForecastData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectorForecastData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="won" fill="#10B981" name="Won" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="weighted" fill="#0047FF" name="Weighted Forecast" stackId="b" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="pipeline" fill="#F59E0B" name="Full Pipeline" stackId="c" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No forecast data. Create deals with sectors to see projections.</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card border border-border p-6 rounded-sm">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Revenue Won</p>
              <p className="text-3xl font-bold mt-2 text-success">{formatCurrency(forecast?.total_won || 0)}</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-sm">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Revenue Lost</p>
              <p className="text-3xl font-bold mt-2 text-destructive">{formatCurrency(forecast?.total_lost || 0)}</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="funnel" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Conversion Funnel</h3>
            <div className="h-80">
              {funnelData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="name" width={100} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                    <Bar dataKey="value" name="Deals">
                      {funnelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No deals in pipeline</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="units" className="space-y-4">
          {units.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-sm">
              <Buildings size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-muted-foreground">No business units yet</p>
              <Button className="mt-4 rounded-sm" onClick={() => { resetForm(); setIsDialogOpen(true); }}>Create First Unit</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {units.map((unit) => (
                <div key={unit.id} className="bg-card border border-border p-6 rounded-sm hover:-translate-y-[1px] transition-transform" data-testid={`bu-${unit.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-semibold">{unit.name}</h3>
                      {unit.sector && <Badge variant="outline" className="mt-1">{PROCUREMENT_SECTORS.find(s => s.id === unit.sector)?.label || unit.sector}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(unit)}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(unit.id)}><Trash size={14} /></Button>
                    </div>
                  </div>
                  {unit.description && <p className="text-sm text-muted-foreground mb-4">{unit.description}</p>}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue Target</p>
                      <p className="font-semibold">{formatCurrency(unit.revenue_target)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Actual Revenue</p>
                      <p className="font-semibold text-success">{formatCurrency(unit.actual_revenue || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Deals</p>
                      <p className="font-semibold">{unit.deal_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Clients</p>
                      <p className="font-semibold">{unit.client_count || 0}</p>
                    </div>
                  </div>
                  {unit.revenue_target > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Target Progress</span>
                        <span>{Math.min(100, ((unit.actual_revenue || 0) / unit.revenue_target * 100)).toFixed(0)}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, ((unit.actual_revenue || 0) / unit.revenue_target * 100))}%` }}></div>
                      </div>
                    </div>
                  )}
                  {unit.services?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {unit.services.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="acquisition" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Contact Acquisition Trend</h3>
              <div className="h-64">
                {acquisition?.contact_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={acquisition.contact_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                      <Area type="monotone" dataKey="contacts" stroke="#0047FF" fill="#0047FF" fillOpacity={0.15} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No data</p>}
              </div>
            </div>
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Lead Score Distribution</h3>
              <div className="h-64 flex items-center">
                {(acquisition?.lead_distribution?.high > 0 || acquisition?.lead_distribution?.medium > 0 || acquisition?.lead_distribution?.low > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={[
                        { name: 'High (80+)', value: acquisition.lead_distribution.high, fill: '#10B981' },
                        { name: 'Medium (40-79)', value: acquisition.lead_distribution.medium, fill: '#F59E0B' },
                        { name: 'Low (<40)', value: acquisition.lead_distribution.low, fill: '#E11D48' },
                      ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground w-full text-center">No leads</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Cross-Sell Tab */}
        <TabsContent value="crosssell" className="space-y-4">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-2">Cross-Sell Opportunities</h3>
            <p className="text-sm text-muted-foreground mb-4">Companies with potential in untapped sectors</p>
          </div>
          {crossSell.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-sm text-muted-foreground">
              <Lightning size={40} className="mx-auto mb-3 opacity-40" />
              <p>No cross-sell opportunities found</p>
              <p className="text-xs mt-1">Add companies with deals across multiple sectors</p>
            </div>
          ) : (
            crossSell.map((opp, i) => (
              <div key={i} className="bg-card border border-border p-4 rounded-sm hover:-translate-y-[1px] transition-transform" data-testid={`crosssell-${i}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{opp.company_name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground">{opp.deal_count} deals</span>
                      <span className="text-sm font-medium">{formatCurrency(opp.current_deal_value)}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">Score: {opp.potential_score?.toFixed(0)}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs text-muted-foreground">Active in:</span>
                  {opp.current_sectors?.map(s => <Badge key={s} className="text-xs bg-emerald-500 text-white">{s}</Badge>)}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-muted-foreground">Opportunity:</span>
                  {opp.opportunity_sectors?.map(s => <Badge key={s} variant="outline" className="text-xs border-primary text-primary">{s}</Badge>)}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Competitive Analysis Tab */}
        <TabsContent value="competitive" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Win/Loss Analysis by Sector</h3>
            {competitive?.sector_analysis && Object.keys(competitive.sector_analysis).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Sector</th>
                      <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Won</th>
                      <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Lost</th>
                      <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Win Rate</th>
                      <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Won Value</th>
                      <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Avg Deal Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(competitive.sector_analysis).map(([sector, data]) => (
                      <tr key={sector} className="border-b border-border">
                        <td className="p-3 font-medium capitalize">{sector}</td>
                        <td className="p-3 text-right text-success">{data.won_count}</td>
                        <td className="p-3 text-right text-destructive">{data.lost_count}</td>
                        <td className="p-3 text-right font-semibold">{data.win_rate?.toFixed(1)}%</td>
                        <td className="p-3 text-right">{formatCurrency(data.won_value)}</td>
                        <td className="p-3 text-right">{formatCurrency(data.avg_deal_size)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-muted-foreground text-center py-8">No closed deals to analyze</p>}
          </div>

          {competitive?.top_leads?.length > 0 && (
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Top Performing Leads</h3>
              <div className="space-y-2">
                {competitive.top_leads.map((lead, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-muted-foreground">#{i + 1}</span>
                      <div>
                        <p className="font-medium text-sm">{lead.name}</p>
                        {lead.email && <p className="text-xs text-muted-foreground">{lead.email}</p>}
                      </div>
                    </div>
                    <Badge className={`${lead.score >= 80 ? 'bg-emerald-500' : lead.score >= 40 ? 'bg-amber-500' : 'bg-zinc-500'} text-white`}>
                      Score: {lead.score}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Business Unit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Edit Business Unit' : 'New Business Unit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="bu-name" />
            </div>
            <div className="space-y-2">
              <Label>Sector</Label>
              <Select value={formData.sector || "none"} onValueChange={(v) => setFormData({ ...formData, sector: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Revenue Target</Label>
                <Input type="number" min="0" value={formData.revenue_target} onChange={(e) => setFormData({ ...formData, revenue_target: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Cost Budget</Label>
                <Input type="number" min="0" value={formData.cost_budget} onChange={(e) => setFormData({ ...formData, cost_budget: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Services</Label>
              <div className="flex gap-2">
                <Input value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} placeholder="Add service" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (serviceInput.trim()) { setFormData({ ...formData, services: [...formData.services, serviceInput.trim()] }); setServiceInput(''); } } }} />
                <Button type="button" variant="outline" onClick={() => { if (serviceInput.trim()) { setFormData({ ...formData, services: [...formData.services, serviceInput.trim()] }); setServiceInput(''); } }}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1">{formData.services.map((s, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({ ...formData, services: formData.services.filter((_, j) => j !== i) })}>{s} x</Badge>)}</div>
            </div>
            <div className="space-y-2">
              <Label>Target Markets</Label>
              <div className="flex gap-2">
                <Input value={marketInput} onChange={(e) => setMarketInput(e.target.value)} placeholder="Add market" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (marketInput.trim()) { setFormData({ ...formData, target_markets: [...formData.target_markets, marketInput.trim()] }); setMarketInput(''); } } }} />
                <Button type="button" variant="outline" onClick={() => { if (marketInput.trim()) { setFormData({ ...formData, target_markets: [...formData.target_markets, marketInput.trim()] }); setMarketInput(''); } }}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1">{formData.target_markets.map((m, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({ ...formData, target_markets: formData.target_markets.filter((_, j) => j !== i) })}>{m} x</Badge>)}</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="bu-submit">{editingUnit ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border p-4 rounded-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
        </div>
        <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
          <Icon size={20} className="text-primary" />
        </div>
      </div>
    </div>
  );
}

function InsightTile({ color = 'blue', icon: Icon, label, value, sub }) {
  return (
    <div className={`fiori-tile fiori-tile-${color}`} data-testid={`insight-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between">
        <Icon size={22} weight="duotone" />
        <span className="text-[10px] tracking-[0.2em] uppercase opacity-75">{label}</span>
      </div>
      <div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        {sub && <p className="text-xs opacity-80 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

